/**
 * Safari-specific redirect loop prevention utilities
 * This module provides aggressive protection against Safari mobile redirect loops
 */

import { isSafari, isMobileSafari } from './browserUtils';

// Global state for Safari redirect protection
let safariRedirectCount = 0;
let lastRedirectPath = '';
let redirectStartTime = 0;
const MAX_REDIRECTS_PER_SECOND = 2;
const REDIRECT_COOLDOWN = 2000; // 2 seconds

/**
 * Initialize Safari redirect protection
 */
export const initSafariRedirectProtection = (): void => {
  if (!isSafari()) return;
  
  console.log('Safari detected - initializing redirect protection');
  
  // Clear any existing redirect state
  sessionStorage.removeItem('safari_redirect_state');
  
  // Set up global redirect monitoring
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;
  
  history.pushState = function(...args) {
    if (shouldBlockRedirect(args[2] as string)) {
      console.warn('Safari redirect protection: Blocking pushState to', args[2]);
      return;
    }
    return originalPushState.apply(this, args);
  };
  
  history.replaceState = function(...args) {
    if (shouldBlockRedirect(args[2] as string)) {
      console.warn('Safari redirect protection: Blocking replaceState to', args[2]);
      return;
    }
    return originalReplaceState.apply(this, args);
  };
  
  // Monitor popstate events
  window.addEventListener('popstate', handlePopState);
};

/**
 * Check if a redirect should be blocked
 */
const shouldBlockRedirect = (path: string): boolean => {
  if (!path) return false;
  
  const currentTime = Date.now();
  
  // If this is the same path as the last redirect, check timing
  if (path === lastRedirectPath) {
    const timeSinceLastRedirect = currentTime - redirectStartTime;
    
    if (timeSinceLastRedirect < REDIRECT_COOLDOWN) {
      safariRedirectCount++;
      
      if (safariRedirectCount > MAX_REDIRECTS_PER_SECOND) {
        console.error('Safari redirect loop detected! Blocking further redirects for', REDIRECT_COOLDOWN, 'ms');
        
        // Store the blocked state
        sessionStorage.setItem('safari_redirect_blocked', JSON.stringify({
          path,
          blockedAt: currentTime,
          redirectCount: safariRedirectCount
        }));
        
        return true;
      }
    } else {
      // Reset counter if enough time has passed
      safariRedirectCount = 0;
      redirectStartTime = currentTime;
    }
  } else {
    // Different path, reset tracking
    safariRedirectCount = 1;
    lastRedirectPath = path;
    redirectStartTime = currentTime;
  }
  
  return false;
};

/**
 * Handle popstate events (back/forward navigation)
 */
const handlePopState = (event: PopStateEvent): void => {
  const currentPath = window.location.pathname;
  
  // Check if we're in a blocked state
  const blockedState = sessionStorage.getItem('safari_redirect_blocked');
  if (blockedState) {
    const blocked = JSON.parse(blockedState);
    const timeSinceBlocked = Date.now() - blocked.blockedAt;
    
    if (timeSinceBlocked < REDIRECT_COOLDOWN) {
      console.warn('Safari redirect protection: Still in cooldown period');
      return;
    } else {
      // Clear blocked state
      sessionStorage.removeItem('safari_redirect_blocked');
      safariRedirectCount = 0;
    }
  }
};

/**
 * Check if Safari is currently in a redirect loop state
 */
export const isSafariRedirectBlocked = (): boolean => {
  if (!isSafari()) return false;
  
  const blockedState = sessionStorage.getItem('safari_redirect_blocked');
  if (!blockedState) return false;
  
  const blocked = JSON.parse(blockedState);
  const timeSinceBlocked = Date.now() - blocked.blockedAt;
  
  return timeSinceBlocked < REDIRECT_COOLDOWN;
};

/**
 * Clear Safari redirect protection state
 */
export const clearSafariRedirectProtection = (): void => {
  safariRedirectCount = 0;
  lastRedirectPath = '';
  redirectStartTime = 0;
  sessionStorage.removeItem('safari_redirect_blocked');
  sessionStorage.removeItem('safari_redirect_state');
};

/**
 * Safe navigation for Safari with built-in loop protection
 */
export const safariSafeRedirect = (path: string): boolean => {
  if (!isSafari()) {
    window.location.href = path;
    return true;
  }
  
  if (isSafariRedirectBlocked()) {
    console.warn('Safari redirect protection: Navigation blocked due to recent loop detection');
    return false;
  }
  
  if (shouldBlockRedirect(path)) {
    return false;
  }
  
  // Use location.replace for Safari to avoid history issues
  setTimeout(() => {
    window.location.replace(path);
  }, 100);
  
  return true;
};

/**
 * Emergency Safari redirect recovery
 */
export const emergencySafariRedirectRecovery = (): void => {
  if (!isSafari()) return;
  
  console.log('Safari emergency redirect recovery initiated');
  
  // Clear all redirect state
  clearSafariRedirectProtection();
  
  // Force reload to break any potential loops
  setTimeout(() => {
    window.location.reload();
  }, 500);
};
